import { type CalendarItem, PageWidgetType } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import {
  type PageWidgetCalendar,
  type PageWidgetCalendarLike,
} from '@efdigital/ll-addon-data/types/content/objects/page-widget-calendar';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import CalendarItemsPicker from '@efdigital/ll-admin-v2/components/pickers/calendar-items-picker.gts';
import {
  type ReactiveStringArray,
  StringArrayResource,
} from '@efdigital/ll-admin-v2/resources/string-array.ts';
import { uniqueId } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { Await } from '@warp-drive/ember';

import type {
  PageWidgetComponentSignature,
  WidgetEditor,
} from '../page-widget-signature.ts';

type CalendarListDisplayType = PageWidgetCalendar['listDisplay'];

const CALENDAR_LIST_DISPLAY_TYPES: Record<CalendarListDisplayType, string> = {
  list: 'List',
  calendar: 'Calendar',
} as const;

const CALENDAR_LIST_DISPLAY_TYPES_ARRAY = Object.entries(
  CALENDAR_LIST_DISPLAY_TYPES
).map(([key, value]) => ({
  key,
  value,
}));

export class PageWidgetCalendarComponent extends Component<PageWidgetComponentSignature> {
  get widget() {
    return this.args.widget as PageWidgetCalendar;
  }

  setListDisplay = (event: Event) => {
    const listDisplay = (event.target as HTMLSelectElement)
      .value as PageWidgetCalendar['listDisplay'];
    this.widget.listDisplay = listDisplay;
  };

  updateCalendarItemIds = (calendarItemIds: string[]) => {
    this.widget.calendarItemIds = calendarItemIds;
  };

  calendarItemsSelected = async (
    calendarItemIds: ReactiveStringArray,
    newValues: CalendarItem[]
  ) => {
    calendarItemIds.updateValues(newValues.map((item) => item.id!));
    for (const item of newValues) {
      await this.args.pageManager.addCalendarItem(item);
    }
  };

  lookupCalendarItems = (calendarItemIds: string[]) => {
    return this.args.pageManager.findCalendarItems(calendarItemIds);
  };

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      {{#let
        (StringArrayResource
          this.widget.calendarItemIds this.updateCalendarItemIds
        )
        as |calendarItemIds|
      }}
        <div class="card-body">
          <div class="form-group mb-4">
            <label for="{{uniqueId}}-calendarItemIds">Calendar entries</label>

            <CalendarItemsPicker
              @programId={{@pageManager.context.programId}}
              @cohortId={{@pageManager.context.cohortId}}
              @calendarItemIds={{calendarItemIds.values}}
              @onSelect={{fn this.calendarItemsSelected calendarItemIds}}
            >
              <:input as |showModal|>
                <div class="list-group mb-3 gap-1">
                  <Await
                    @promise={{this.lookupCalendarItems calendarItemIds.values}}
                  >
                    <:success as |calendarItems|>
                      {{#each calendarItems as |calendarItem index|}}
                        <div class="input-group">
                          <input
                            type="text"
                            class="form-control list-group-item rounded-start-1
                              {{if
                                calendarItem.cancelled
                                'text-decoration-line-through text-danger'
                              }}"
                            value={{calendarItem.title}}
                            aria-label="Calendar entry"
                            readonly
                          />
                          <button
                            aria-label="Remove calendar entry"
                            class="btn btn-outline-primary rounded-end-1"
                            type="button"
                            {{on
                              "click"
                              (fn calendarItemIds.removeValueAt index)
                            }}
                          ><iconify-icon inline icon="ph:x-bold" /></button>
                        </div>
                      {{/each}}
                    </:success>
                  </Await>
                </div>
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  {{on "click" (fn showModal calendarItemIds.values)}}
                >
                  <iconify-icon inline icon="ph:plus-bold" />
                  Select calendar entries
                </button>
              </:input>
            </CalendarItemsPicker>
          </div>

          <div class="form-group mb-4">
            <label for="{{uniqueId}}-listDisplay">Display calendar entries as</label>
            <select
              class="form-control"
              id="{{uniqueId}}-listDisplay"
              {{selectValue this.widget.listDisplay}}
              {{on "change" this.setListDisplay}}
            >
              {{#each CALENDAR_LIST_DISPLAY_TYPES_ARRAY as |listDisplay|}}
                <option value={{listDisplay.key}}>{{listDisplay.value}}</option>
              {{/each}}
            </select>
          </div>
        </div>
      {{/let}}
    {{/let}}
  </template>
}

const DEFAULT_WIDGET_CALENDAR: Omit<PageWidgetCalendarLike, 'wid'> = {
  widgetType: PageWidgetType.Calendar,
  title: 'Calendar',
  css: '',
  calendarItemIds: [],
  listDisplay: 'list',
};

export const CalendarWidgetEditor: WidgetEditor = {
  icon: 'ph:calendar-dots-bold',
  label: 'Calendar',
  component: PageWidgetCalendarComponent,
  dataType: DataTypeContent.PageWidgetCalendar,
  widgetData: DEFAULT_WIDGET_CALENDAR,
};
